import React, { memo, useCallback } from 'react';
import { View } from 'react-native';
import { Line, Svg } from 'react-native-svg';
import { CardView, rem, Spacer } from 'design-system';
import { StaticLineChart } from 'design-system/LineChart/LineChart';
import Text from 'design-system/Text';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import createStyleSheets from 'utils/createStyleSheets';
const FULL_DISCLAIMER = '£10,000 invested since Jan 2021. This is an example for illustration only. Past performance is not a reliable indicator of future results. Returns are not guaranteed.';
const SP500_POINTS = [
    { x: 0.0017, y: 1.0 },
    { x: 0.038, y: 0.9688 },
    { x: 0.0575, y: 0.9176 },
    { x: 0.074, y: 0.9005 },
    { x: 0.0927, y: 0.8574 },
    { x: 0.1075, y: 0.8696 },
    { x: 0.1455, y: 0.7855 },
    { x: 0.161, y: 0.7651 },
    { x: 0.1814, y: 0.8398 },
    { x: 0.1958, y: 0.8522 },
    { x: 0.2157, y: 0.7833 },
    { x: 0.234, y: 0.8219 },
    { x: 0.2492, y: 0.8473 },
    { x: 0.2665, y: 0.8947 },
    { x: 0.2862, y: 0.813 },
    { x: 0.3019, y: 0.7979 },
    { x: 0.3209, y: 0.8339 },
    { x: 0.3392, y: 0.8106 },
    { x: 0.3544, y: 0.8227 },
    { x: 0.3763, y: 0.8713 },
    { x: 0.3936, y: 0.8368 },
    { x: 0.4409, y: 0.8256 },
    { x: 0.4657, y: 0.799 },
    { x: 0.4851, y: 0.7465 },
    { x: 0.5001, y: 0.7267 },
    { x: 0.5176, y: 0.7224 },
    { x: 0.5357, y: 0.7351 },
    { x: 0.5499, y: 0.7588 },
    { x: 0.5807, y: 0.6497 },
    { x: 0.5973, y: 0.6203 },
    { x: 0.6147, y: 0.5456 },
    { x: 0.6332, y: 0.5059 },
    { x: 0.6514, y: 0.5322 },
    { x: 0.6672, y: 0.5206 },
    { x: 0.6876, y: 0.4289 },
    { x: 0.7222, y: 0.4557 },
    { x: 0.7371, y: 0.4492 },
    { x: 0.7588, y: 0.3779 },
    { x: 0.7757, y: 0.2751 },
    { x: 0.7902, y: 0.2767 },
    { x: 0.8096, y: 0.2245 },
    { x: 0.8276, y: 0.2974 },
    { x: 0.8458, y: 0.4357 },
    { x: 0.8587, y: 0.483 },
    { x: 0.8824, y: 0.401 },
    { x: 0.9003, y: 0.3469 },
    { x: 0.9146, y: 0.2407 },
    { x: 0.9253, y: 0.2435 },
    { x: 0.9709, y: 0.0909 },
    { x: 1.0, y: 0.1035 },
];
const CASH_POINTS = [
    { x: 0.0, y: 1.0 },
    { x: 0.0192, y: 0.9999 },
    { x: 0.0385, y: 0.9998 },
    { x: 0.0577, y: 0.9997 },
    { x: 0.0769, y: 0.9996 },
    { x: 0.0962, y: 0.9995 },
    { x: 0.1154, y: 0.9995 },
    { x: 0.1346, y: 0.9994 },
    { x: 0.1538, y: 0.9992 },
    { x: 0.1731, y: 0.999 },
    { x: 0.1923, y: 0.9987 },
    { x: 0.2115, y: 0.9983 },
    { x: 0.2308, y: 0.9975 },
    { x: 0.25, y: 0.9968 },
    { x: 0.2692, y: 0.9958 },
    { x: 0.2885, y: 0.9946 },
    { x: 0.3077, y: 0.9934 },
    { x: 0.3269, y: 0.9917 },
    { x: 0.3462, y: 0.99 },
    { x: 0.3654, y: 0.9878 },
    { x: 0.3846, y: 0.9849 },
    { x: 0.4038, y: 0.9816 },
    { x: 0.4231, y: 0.9782 },
    { x: 0.4423, y: 0.9743 },
    { x: 0.4615, y: 0.9704 },
    { x: 0.4808, y: 0.9662 },
    { x: 0.5, y: 0.9618 },
    { x: 0.5192, y: 0.9574 },
    { x: 0.5385, y: 0.9525 },
    { x: 0.5577, y: 0.9473 },
    { x: 0.5769, y: 0.9421 },
    { x: 0.5962, y: 0.9368 },
    { x: 0.6154, y: 0.9315 },
    { x: 0.6346, y: 0.9263 },
    { x: 0.6538, y: 0.921 },
    { x: 0.6731, y: 0.9157 },
    { x: 0.6923, y: 0.9104 },
    { x: 0.7115, y: 0.905 },
    { x: 0.7308, y: 0.8996 },
    { x: 0.75, y: 0.8942 },
    { x: 0.7692, y: 0.8888 },
    { x: 0.7885, y: 0.8835 },
    { x: 0.8077, y: 0.8784 },
    { x: 0.8269, y: 0.8731 },
    { x: 0.8462, y: 0.8681 },
    { x: 0.8654, y: 0.8631 },
    { x: 0.8846, y: 0.858 },
    { x: 0.9038, y: 0.853 },
    { x: 0.9231, y: 0.8482 },
    { x: 0.9423, y: 0.8433 },
    { x: 0.9615, y: 0.8385 },
    { x: 0.9808, y: 0.8338 },
    { x: 1.0, y: 0.8292 },
];
const CHART_HEIGHT = rem(200);
const InvestPerformanceCard = ({ variant = 'full' }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const handleLearnMore = useCallback(() => {
        navigation.navigate('PerformanceExample');
    }, []);
    const { width: screenWidth } = useAppFrameDimensions(true);
    const chartWidth = screenWidth - rem(108);
    const svgHeight = CHART_HEIGHT + 6;
    return (<CardView withPadding style={styles.graphCard}>
      <Spacer h={6}/>
      <View style={styles.chartContainer}>
        <View style={[styles.yAxisLabels, { height: svgHeight + rem(24) }]}>
          <Text NumbersThin-14 Secondary>
            £20k
          </Text>
          <Text NumbersThin-14 Secondary>
            £18k
          </Text>
          <Text NumbersThin-14 Secondary>
            £16k
          </Text>
          <Text NumbersThin-14 Secondary>
            £14k
          </Text>
          <Text NumbersThin-14 Secondary>
            £12k
          </Text>
          <Text NumbersThin-14 Secondary>
            £10k
          </Text>
        </View>
        <View style={[{ height: CHART_HEIGHT + 10 }]}>
          <StaticLineChart showEndPoint width={chartWidth} points={SP500_POINTS} points2={CASH_POINTS} height={CHART_HEIGHT} strokeColor={colors.text.brand} line2StrokeColor={colors.elements.primary}/>
          <Svg style={[styles.dottedLineView, { height: svgHeight }]} width={chartWidth} height={svgHeight}>
            {Array.from({ length: 6 }, (_, i) => {
            const y = Math.round((i / 5) * svgHeight);
            return (<Line key={i} x1={0} y1={y} y2={y} x2={chartWidth} strokeWidth={1} strokeDasharray="2 6" stroke={colors.elements.secondaryTransparent}/>);
        })}
          </Svg>
          <View style={styles.endLabelsContainer}>
            <View style={[styles.endLabel, styles.endLabelBrand]}>
              <Text NumbersThin-14 Secondary>
                £18,717.11
              </Text>
            </View>
            <View style={[styles.endLabel, styles.endLabelPrimary]}>
              <Text NumbersThin-14 Secondary>
                £11,656.86
              </Text>
            </View>
          </View>
        </View>
      </View>
      <View style={styles.xAxisLabels}>
        <Text Text-14 Secondary>
          2021
        </Text>
        <Text Text-14 Secondary>
          2025
        </Text>
      </View>
      <Spacer h={12}/>
      <View style={styles.legendRow}>
        <View style={[styles.legendDot, styles.brandDot]}/>
        <Text Text-16>iShares Core S&P 500</Text>
        <Spacer w={16}/>
        <View style={[styles.legendDot, styles.primaryDot]}/>
        <Text Text-16>Cash</Text>
      </View>
      <Spacer h={16}/>
      <Text TextThin-14 Secondary centered>
        {FULL_DISCLAIMER}
        {variant === 'full' && (<Text Text-14 Primary onPress={handleLearnMore} suppressHighlighting>
            {' '}
            Learn more
          </Text>)}
      </Text>
    </CardView>);
};
export default memo(InvestPerformanceCard);
const styleSet = createStyleSheets((colors) => ({
    graphCard: {
        marginHorizontal: rem(16),
        marginVertical: rem(8),
    },
    chartContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    yAxisLabels: {
        justifyContent: 'space-between',
        marginRight: rem(16),
    },
    endLabelsContainer: {
        position: 'absolute',
        right: -rem(6),
        top: rem(-12),
        bottom: rem(48),
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    endLabel: {
        paddingHorizontal: rem(8),
        paddingVertical: rem(4),
        borderRadius: rem(6),
    },
    endLabelBrand: {
        backgroundColor: colors.background.dark,
    },
    endLabelPrimary: {
        backgroundColor: colors.background.dark,
    },
    xAxisLabels: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingLeft: rem(44),
        marginTop: rem(4),
    },
    legendRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    legendDot: {
        width: rem(8),
        height: rem(8),
        borderRadius: rem(4),
        marginRight: rem(6),
    },
    brandDot: {
        backgroundColor: colors.text.brand,
    },
    primaryDot: {
        backgroundColor: colors.elements.primary,
    },
    dottedLineView: {
        top: 0,
        left: 0,
        right: 0,
        position: 'absolute',
    },
}));
